import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { DefaultPosition } from '@sorare/core/src/assets/icons/DefaultPosition';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import type { LineupStyle } from '@sorare/core/src/components/lobby/ContenderLayout/types';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { arrayToObject, range } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { getSortedAppearancesFromRules } from '@sorare/core/src/lib/players';
import { Grade } from '@sorare/marketplace/src/components/player/Grade';
import { InjuryTag } from '@sorare/marketplace/src/components/player/InjuryTag';

import { AppearanceRuleIcon } from '#src/components/lineup/AppearanceRuleIcon/index.tsx';
import { PlayerCard } from '#src/components/lineup/PlayerCard/index.tsx';
import { SimpleCardPlaceholder } from '#src/components/lineup/SimpleCardPlaceholder/index.tsx';
import { So5AppearanceDialog } from '#src/components/stats/So5AppearanceDialog/index.tsx';
import { getLineupSwapMetadata } from '#src/hooks/lineup/useLineupSwap.ts';
import {
  getBestEligibleProjection,
  getComposeTeamRoute,
  isAppearanceLocked,
  isFixtureLive,
  isFixtureOpened,
} from '#src/lib/so5.ts';

import type { So5Lineup_so5LeaderboardContender } from './__generated__/index.graphql.ts';
import { useLineupDragAndDrop } from './useLineupDragAndDrop';
import type { SwapPayload, SwapSlot } from './useLineupDragAndDrop';

const Slot = styled.div<{
  $droppable?: boolean;
  $isSwapping?: boolean;
  $isDragActive?: boolean;
  $isBeingDragged?: boolean;
}>`
  margin: 0 auto auto;
  width: 100%;
  height: 100%;
  min-width: 0;
  position: relative;
  isolation: isolate;

  ${props =>
    props.$droppable &&
    props.$isDragActive &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      aspect-ratio: var(--card-aspect-ratio);
      border-radius: 4px;
      border: 2px dashed rgba(var(--c-rgb-superRare-secondary), 1);
      pointer-events: none;
      z-index: 1;
    }
  `}

  ${props =>
    !props.$droppable &&
    props.$isDragActive &&
    `
    opacity: 0.2;
  `}

  ${props =>
    props.$isSwapping &&
    `
    opacity: 0.5;
  `}

  ${props =>
    props.$isBeingDragged &&
    `
    > :first-child {
      opacity: 0;
      pointer-events: none;
    }
  `}
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
  z-index: 1;
`;
const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-and-a-half-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

type Props = {
  so5LeaderboardContender?: So5Lineup_so5LeaderboardContender;
  lineupStyle?: LineupStyle;
  className?: string;
  fillHeightPercent?: number;
  displayLiveEdit?: boolean;
  onSwap?: (source: SwapSlot, target: SwapSlot) => void;
  swappingSlots?: string[];
  draggedPayload?: SwapPayload | null;
  setDraggedPayload?: (payload: SwapPayload | null) => void;
};

export const So5Lineup = ({
  so5LeaderboardContender,
  lineupStyle,
  className,
  fillHeightPercent,
  displayLiveEdit,
  onSwap,
  swappingSlots,
  draggedPayload,
  setDraggedPayload,
}: Props) => {
  const { isMe } = useCurrentUserContext();
  const openDialog = useOpenDialog();
  const { sport } = useSportContext();
  const [so5Appearance, setSo5Appearance] = useState<
    | NonNullable<
        So5Lineup_so5LeaderboardContender['so5Lineup']
      >['so5Appearances'][0]
    | undefined
  >();
  const [localDraggedPayload, setLocalDraggedPayload] =
    useState<SwapPayload | null>(null);
  const [delayedDraggedPayload, setDelayedDraggedPayload] =
    useState<SwapPayload | null>(null);
  const sharedDraggedPayload = draggedPayload ?? localDraggedPayload;
  const setSharedDraggedPayload = setDraggedPayload ?? setLocalDraggedPayload;
  const isDragActive = sharedDraggedPayload !== null;
  const sharedDraggedPayloadRef = useRef(sharedDraggedPayload);

  // Keep ref in sync with state
  useEffect(() => {
    sharedDraggedPayloadRef.current = sharedDraggedPayload;
  }, [sharedDraggedPayload]);

  const handleDragCapture = useCallback(() => {
    // Called after browser has captured drag ghost image
    // Use ref to get current value without closure issues
    setDelayedDraggedPayload(sharedDraggedPayloadRef.current);
  }, []);

  // Clear delayed payload when drag ends
  if (!sharedDraggedPayload && !!delayedDraggedPayload) {
    setDelayedDraggedPayload(null);
  }

  const withInlineGamesDots =
    so5LeaderboardContender?.so5Leaderboard.so5Fixture.sport !== Sport.FOOTBALL;

  const { formatMessage } = useIntl();
  const { so5Lineup, so5Leaderboard, managerTeam } =
    so5LeaderboardContender || {};
  const seasonBonusRule = (
    so5Leaderboard?.displayedTypedRules as
      | {
          __typename?: string;
          boundaries?: { min?: number | null; max?: number | null };
        }[]
      | undefined
  )?.find(rule => rule.__typename === 'So5SeasonBonusRule');
  const seasonBonusRuleMin = seasonBonusRule?.boundaries?.min ?? undefined;
  const seasonBonusRuleMax = seasonBonusRule?.boundaries?.max ?? undefined;
  const lineupId = so5Lineup?.id;
  const leagueSlug = (
    so5Leaderboard as { so5League?: { slug: string } } | undefined
  )?.so5League?.slug;
  const { getDragProps, getDropHandlers, draggedSwapPayload } =
    useLineupDragAndDrop({
      onSwap,
      lineupId,
      leagueSlug,
      draggedPayload: sharedDraggedPayload,
      setDraggedPayload: setSharedDraggedPayload,
      onDragCapture: handleDragCapture,
    });

  const appearances =
    so5Leaderboard &&
    getSortedAppearancesFromRules({
      so5Lineup: so5Lineup || undefined,
      so5Leaderboard,
    });
  const lineupInSeasonEligible = appearances?.map(appearance => {
    if (!isType(appearance, 'So5Appearance')) return false;
    return !!appearance.anyCard?.inSeasonEligible;
  });
  const {
    lineupAverageScores,
    mvpBenchIds,
    remainingPointsForSlotByIndex,
    averageScoresCap,
    sumOfAverageScoresCap,
    immuneAppearances,
  } = useMemo(
    () => getLineupSwapMetadata({ contender: so5LeaderboardContender, sport }),
    [so5LeaderboardContender, sport]
  );
  const card =
    so5Appearance && 'anyCard' in so5Appearance
      ? so5Appearance.anyCard
      : undefined;

  const eligibleCardsCountByPosition = arrayToObject({
    value: c => c,
    key: c => c.position,
    list: so5Leaderboard?.eligibleCardsCountByPosition || [],
  }) as Record<
    Position,
    So5Lineup_so5LeaderboardContender['so5Leaderboard']['eligibleCardsCountByPosition'][number]
  >;

  const userSlug = managerTeam?.user?.slug || so5Lineup?.user.slug;

  const isLive = isFixtureLive(so5Leaderboard?.so5Fixture);
  const isUpcoming = isFixtureOpened(so5Leaderboard?.so5Fixture);

  const canLiveEditLineup =
    displayLiveEdit &&
    isMe(userSlug) &&
    so5Lineup?.updatable &&
    isFixtureLive(so5Leaderboard?.so5Fixture) &&
    so5Leaderboard?.canCompose?.value &&
    !so5Lineup?.cancelledAt &&
    !appearances?.every(
      a => isType(a, 'So5Appearance') && isAppearanceLocked(a)
    );

  const numberOfPlaceholderNeeded = sport === Sport.BASEBALL ? 7 : 5;

  return (
    <>
      <LineupCardsLayout
        fillHeightPercent={fillHeightPercent}
        lineupStyle={lineupStyle}
        slots={appearances?.length}
        className={className}
        cancelled={!!so5Lineup?.cancelledAt}
        gap={lineupStyle !== 'spread' ? 0.5 : 1}
      >
        {appearances?.map((appearance, i) => {
          const appearanceRule = so5Leaderboard?.rules?.appearances?.at(i);

          if (!appearanceRule) {
            return null;
          }

          const position =
            (appearanceRule.positions.length === 1 &&
              appearanceRule.positions[0]) ||
            null;

          const count =
            position && eligibleCardsCountByPosition[position]
              ? eligibleCardsCountByPosition[position].totalCount -
                eligibleCardsCountByPosition[position].usedCardsCount
              : undefined;

          const composeTeamPath = so5Leaderboard?.slug
            ? getComposeTeamRoute({
                leaderboardSlug: so5Leaderboard?.slug,
                eventType: so5Leaderboard?.so5Fixture.type.toLowerCase(),
                managerTeamId: managerTeam?.id,
                lineupId: so5Lineup?.id,
                sport,
              })
            : '';

          const projection =
            'index' in appearance &&
            getBestEligibleProjection(appearance.eligiblePlayerGameScores);

          const canBeSwapped =
            isType(appearance, 'So5Appearance') &&
            !isAppearanceLocked(appearance);
          const slotAverageScore = lineupAverageScores[i];
          const slotInSeasonEligible = isType(appearance, 'So5Appearance')
            ? !!appearance.anyCard?.inSeasonEligible
            : undefined;

          const slotKey = lineupId ? `${lineupId}:${i}` : '';
          const isSwapping = !!slotKey && !!swappingSlots?.includes(slotKey);
          const { isDroppable, handleDragOver, handleDrop } = getDropHandlers({
            canBeSwapped,
            appearanceRulePositions: appearanceRule?.positions,
            seasonBonusRuleMin,
            seasonBonusRuleMax,
            inSeasonEligible: slotInSeasonEligible,
            lineupInSeasonEligible,
            targetAverageScore: slotAverageScore,
            maxIncomingAverageScore: remainingPointsForSlotByIndex[i],
            targetIsMVP:
              sport === Sport.NBA &&
              isType(appearance, 'So5Appearance') &&
              mvpBenchIds.has(appearance.id),
            lineupAverageScores,
            averageScoresCap,
            sumOfAverageScoresCap,
            immuneAppearances,
            slotIndex: i,
          });
          const hasAverageScoresRule = Boolean(
            so5Leaderboard?.displayedTypedRules?.some(
              rule => rule.__typename === 'So5AverageScoresRule'
            )
          );
          const sumOfAverageScoresRule =
            (so5Leaderboard?.rules as { sumOfAverageScores?: number | null })
              ?.sumOfAverageScores ?? null;
          const hasSumOfAverageScoresRule =
            typeof sumOfAverageScoresRule === 'number';
          const shouldShowAverageScoreOnDrag =
            sport === Sport.NBA ||
            hasAverageScoresRule ||
            hasSumOfAverageScoresRule;
          const showAverageScoreForSwap =
            shouldShowAverageScoreOnDrag && !!draggedSwapPayload;
          const isMVPForLineup =
            sport === Sport.NBA &&
            isType(appearance, 'So5Appearance') &&
            mvpBenchIds.has(appearance.id);
          const dragProps = getDragProps({
            canBeSwapped,
            isSwapping,
            cardPositions: isType(appearance, 'So5Appearance')
              ? [appearance.position]
              : undefined,
            seasonBonusRuleMin,
            seasonBonusRuleMax,
            inSeasonEligible: slotInSeasonEligible,
            lineupInSeasonEligible,
            averageScore: slotAverageScore,
            maxIncomingAverageScore: remainingPointsForSlotByIndex[i],
            isMVP:
              sport === Sport.NBA &&
              isType(appearance, 'So5Appearance') &&
              mvpBenchIds.has(appearance.id),
            lineupAverageScores,
            averageScoresCap,
            sumOfAverageScoresCap,
            immuneAppearances,
            slotIndex: i,
          });

          const isBeingDragged =
            delayedDraggedPayload?.lineupId === lineupId &&
            delayedDraggedPayload?.slotIndex === i;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Slot
              key={i}
              $droppable={isDroppable}
              $isSwapping={isSwapping}
              $isDragActive={isDragActive}
              $isBeingDragged={isBeingDragged}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {isSwapping ? (
                <LoadingOverlay>
                  <LoadingIndicator smaller />
                </LoadingOverlay>
              ) : null}
              {'index' in appearance ? (
                <>
                  <PlayerCard
                    appearance={appearance}
                    showScore
                    showAverageScore={showAverageScoreForSwap}
                    isMVP={isMVPForLineup}
                    showMVPFrame={Boolean(isMVPForLineup && isDragActive)}
                    withDate
                    withInlineGamesDots={withInlineGamesDots}
                    onClick={() => {
                      setSo5Appearance(appearance);
                    }}
                    displayLiveEdit={canLiveEditLineup}
                    editPath={composeTeamPath}
                    dragProps={dragProps}
                    injuryTagNode={
                      !!(isUpcoming || isLive) && (
                        <InjuryTag player={appearance.anyCard?.anyPlayer} />
                      )
                    }
                    gradeNode={
                      !!projection &&
                      !!isUpcoming && (
                        <Grade size={2.5} projection={projection} />
                      )
                    }
                  />
                  {isBeingDragged ? (
                    <Link
                      aria-label={formatMessage(fantasy.composeLineup)}
                      to={getComposeTeamRoute({
                        leaderboardSlug: so5Leaderboard?.slug || '',
                        lineupId: so5Lineup?.id,
                        managerTeamId: managerTeam?.id,
                        sport,
                        eventType:
                          so5Leaderboard?.so5Fixture.type.toLowerCase() || '',
                      })}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                      }}
                    >
                      <SimpleCardPlaceholder
                        icon={
                          <AppearanceRuleIcon appearanceRule={appearanceRule} />
                        }
                        label=""
                        count={count}
                        loading={!so5LeaderboardContender}
                        Footer={
                          lineupStyle === 'spread' ? SlotFooter : undefined
                        }
                      />
                    </Link>
                  ) : null}
                </>
              ) : (
                <Link
                  aria-label={formatMessage(fantasy.composeLineup)}
                  to={getComposeTeamRoute({
                    leaderboardSlug: so5Leaderboard?.slug || '',
                    lineupId: so5Lineup?.id,
                    managerTeamId: managerTeam?.id,
                    sport,
                    eventType:
                      so5Leaderboard?.so5Fixture.type.toLowerCase() || '',
                  })}
                >
                  <SimpleCardPlaceholder
                    icon={
                      <AppearanceRuleIcon appearanceRule={appearanceRule} />
                    }
                    label=""
                    count={count}
                    loading={!so5LeaderboardContender}
                    Footer={lineupStyle === 'spread' ? SlotFooter : undefined}
                  />
                </Link>
              )}
            </Slot>
          );
        }) ||
          range(numberOfPlaceholderNeeded).map(i => (
            <Slot key={i}>
              <SimpleCardPlaceholder
                icon={<DefaultPosition />}
                label=""
                Footer={lineupStyle === 'spread' ? SlotFooter : undefined}
                loading={false}
              />
            </Slot>
          ))}
      </LineupCardsLayout>
      {!!so5Appearance && (
        <So5AppearanceDialog
          open
          onClose={() => setSo5Appearance(undefined)}
          showUpsell={!card}
          so5AppearanceId={so5Appearance.id}
          onClickGame={id =>
            openDialog(DialogKey.game, id, {
              additionalParams: {
                [DialogKey.eventType]:
                  so5LeaderboardContender?.so5Leaderboard.so5Fixture.type || '',
              },
            })
          }
        />
      )}
    </>
  );
};

So5Lineup.fragments = {
  so5LeaderboardContender: gql`
    fragment So5Lineup_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        updatable
        cancelledAt
        so5Appearances {
          id
          index
          position
          captain
          boosted
          anyCard {
            slug
            inSeasonEligible
            anyPlayer {
              slug
              ...InjuryTag_anyPlayer
            }
          }
          eligiblePlayerGameScores {
            id
            projection {
              id
              ...Grade_playerGameScoreProjection
            }
            ...getBestProjection_playerGameScoreInterface
          }
          ...PlayerCard_composeTeamBenchObjectInterface
          ...isAppearanceLocked_so5Appearance
        }
        user {
          slug
        }
        ...getSortedAppearancesFromRules_so5Lineup
      }
      managerTeam {
        id
        user {
          slug
        }
      }
      so5Leaderboard {
        slug
        id
        displayedTypedRules {
          id
          ... on So5AverageScoresRule {
            id
            max
            immuneAppearances
          }
          ... on So5SeasonBonusRule {
            id
            boundaries {
              min
              max
            }
          }
        }
        so5League {
          slug
        }
        mainRarityType
        canCompose {
          value
        }
        so5Fixture {
          slug
          eventType
          type
          sport
          ...isFixtureLive_so5Fixture
          ...isFixtureOpened_so5Fixture
        }
        rules {
          id
          sumOfAverageScores
          appearances {
            id
            name
            positions
            ...AppearanceRuleIcon_appearanceRule
          }
        }
        eligibleCardsCountByPosition {
          slug
          position
          totalCount
          usedCardsCount
        }
        ...getSortedAppearancesFromRules_so5Leaderboard
      }
    }
    ${PlayerCard.fragments.composeTeamBenchObjectInterface}
    ${isAppearanceLocked.fragments.so5Appearance}
    ${getSortedAppearancesFromRules.fragments.so5Lineup}
    ${getSortedAppearancesFromRules.fragments.so5Leaderboard}
    ${AppearanceRuleIcon.fragments.appearanceRule}
    ${isFixtureLive.fragments.so5Fixture}
    ${InjuryTag.fragments.anyPlayer}
    ${isFixtureOpened.fragments.so5Fixture}
    ${getBestEligibleProjection.fragments.playerGameScoreInterface}
    ${Grade.fragments.projection}
  ` as TypedDocumentNode<So5Lineup_so5LeaderboardContender>,
};
